import { BadgeCheck, CalendarClock } from "lucide-react";

import {
  answeredAgentRequest,
  approveAgentRequest,
  cancelAgentRequest,
  declineAgentRequest,
  dismissAgentRequest,
} from "@/api/client";
import { AgentRequestView } from "@/api/types";

// ------------------------------------------------------------------ one request

export function RequestCard({
  row,
  busy,
  onAct,
}: {
  row: AgentRequestView;
  busy: string | null;
  onAct: (key: string, action: () => Promise<unknown>) => void;
}) {
  const decision = row.decision;
  const holdId = decision?.hold_id;
  const awaiting = decision?.state === "AWAITING_YOU";
  const consider = decision?.state === "CONSIDER";
  // Their address, so the host can write to them. Verified or merely handed over, it is the one
  // they agreed to share (D-92) and either is somewhere a reply can go.
  const replyTo = row.caller.verified?.email ?? row.caller.stated?.email ?? null;

  return (
    <div
      className={`rounded-md border bg-background p-3 ${
        awaiting ? "border-2 border-black" : consider ? "border-2 border-brand-blue" : "border-border"
      }`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Identity caller={row.caller} />
        <span className="text-[11px] text-muted-foreground">{when(row.received_at)}</span>
      </div>

      {row.purpose?.stated && (
        // Their words, quoted. Paraphrasing what somebody said they wanted, on the screen where
        // you decide whether to meet them, would be putting words in their mouth.
        <p className="mt-2 text-sm">“{row.purpose.stated}”</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span className="rounded-full border border-border px-2 py-0.5">{outcomeLabel(row.outcome)}</span>
        {row.reason_code && refusalLabel(row.reason_code) && (
          <span className="rounded-full border border-border px-2 py-0.5">
            {refusalLabel(row.reason_code)}
          </span>
        )}
        {row.purpose?.classified && (
          <span className="rounded-full border border-border px-2 py-0.5">
            {`${row.purpose.classified}${confidenceNote(row.purpose)}`}
          </span>
        )}
        {decision?.starts_at && (
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="h-3 w-3" aria-hidden />
            {slot(decision.starts_at, decision.ends_at)}
          </span>
        )}
      </div>

      {awaiting && decision?.expires_at && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          Held until {when(decision.expires_at)}
        </p>
      )}
      {row.reason_code && refusalNote(row.reason_code) && (
        <p className="mt-1 text-[11px] text-muted-foreground">{refusalNote(row.reason_code)}</p>
      )}
      {consider && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          They have not been told any of this, and they are not waiting on you.
        </p>
      )}
      {decision?.state === "LAPSED" && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          The hold lapsed before it was answered.
        </p>
      )}
      {decision?.state === "MOVING" && (
        // The times above are still the ones that stand. Nothing has been given up in order to
        // ask about a different hour, and if the move comes to nothing this is the meeting (D-82).
        <p className="mt-1 text-[11px] text-muted-foreground">
          They are looking for a different time. This one still stands until they find one.
        </p>
      )}

      {consider && decision?.consideration_id && (
        <div className="mt-3 flex flex-wrap gap-2">
          {replyTo && (
            // Their own mail client, their own address, their own words. A reply box here would
            // make us the channel: storing what a host wrote to a stranger, delivering it, and
            // owning what happened next (D-94).
            <a
              href={mailTo(replyTo, row.purpose?.stated)}
              onClick={() =>
                onAct(row.id, () => answeredAgentRequest(decision.consideration_id as string))
              }
              className="rounded border-2 border-brand-blue bg-brand-blue px-3 py-1 text-[11px] font-comic-headline uppercase tracking-wide text-white"
            >
              Get in touch
            </a>
          )}
          <button
            type="button"
            disabled={busy === row.id}
            onClick={() =>
              onAct(row.id, () => dismissAgentRequest(decision.consideration_id as string))
            }
            className="rounded border border-border px-3 py-1 text-[11px] font-comic-headline uppercase tracking-wide hover:bg-yellow-100 disabled:opacity-60"
          >
            Dismiss
          </button>
        </div>
      )}

      {(awaiting || decision?.state === "BOOKED" || decision?.state === "MOVING") && (
        <div className="mt-3 flex flex-wrap gap-2">
          {awaiting && holdId && (
            <>
              <button
                type="button"
                disabled={busy === row.id}
                onClick={() => onAct(row.id, () => approveAgentRequest(holdId))}
                className="rounded border-2 border-black bg-brand-blue px-3 py-1 text-[11px] font-comic-headline uppercase tracking-wide text-white disabled:opacity-60"
              >
                Book it
              </button>
              <button
                type="button"
                disabled={busy === row.id}
                onClick={() => onAct(row.id, () => declineAgentRequest(holdId))}
                className="rounded border border-border px-3 py-1 text-[11px] font-comic-headline uppercase tracking-wide hover:bg-yellow-100 disabled:opacity-60"
              >
                Decline
              </button>
            </>
          )}
          {(decision?.state === "BOOKED" || decision?.state === "MOVING") && row.session_id && (
            <button
              type="button"
              disabled={busy === row.id}
              onClick={() => onAct(row.id, () => cancelAgentRequest(row.session_id as string))}
              className="rounded border border-border px-3 py-1 text-[11px] font-comic-headline uppercase tracking-wide hover:bg-red-50 hover:text-red-700 disabled:opacity-60"
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The caller, with the two kinds of claim kept apart.
 *
 * <p>A verified address gets the mark; anything the caller merely typed is labelled as such. The
 * marking goes on the trustworthy half deliberately — an interface that decorates everything and
 * omits the badge where it does not apply asks the reader to notice an absence, and people do not.</p>
 */
export function Identity({ caller }: { caller: AgentRequestView["caller"] }) {
  const verified = caller.verified?.email;
  const stated = caller.stated;

  return (
    <div className="min-w-0">
      <p className="truncate text-sm font-medium">
        {stated?.name ?? verified ?? "Someone"}
        {stated?.name && <span className="ml-1 text-[11px] text-muted-foreground">(they say)</span>}
      </p>
      <p className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
        {verified ? (
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <BadgeCheck className="h-3 w-3" aria-hidden />
            {verified}
          </span>
        ) : (
          stated?.email && <span>{stated.email} (unverified)</span>
        )}
        <span>via {providerLabel(caller.provider)}</span>
        {caller.verified?.tenant && <span>· tenant {caller.verified.tenant}</span>}
      </p>
    </div>
  );
}

// ------------------------------------------------------------------ words

/**
 * How the purpose was arrived at, in words (D-66).
 *
 * <p>Only ever said of a purpose that was *not* stated. A host reading their own inbox is deciding
 * by hand, and the thing they need is whether the person said what they wanted or whether we worked
 * it out — so the unremarkable case says nothing and the two shakier ones say which they are.</p>
 */
export function confidenceNote(purpose: { stated_explicitly: boolean; confidence?: string }): string {
  if (purpose.stated_explicitly) return "";
  switch (purpose.confidence) {
    case "MEDIUM":
      return " (inferred, though their words carried it)";
    case "LOW":
      return " (inferred from context)";
    default:
      return " (inferred)";
  }
}

/**
 * A message the host can send from their own client, addressed and titled but not written.
 *
 * <p>Their words are theirs. Prefilling a body would be us drafting a stranger's reply for them,
 * and a host who sends it unread has said something they did not mean (D-94).</p>
 */
function mailTo(address: string, asked?: string | null): string {
  const subject = asked ? `Re: ${asked}` : "Your request to meet";
  return `mailto:${encodeURIComponent(address)}?subject=${encodeURIComponent(subject)}`;
}

export function outcomeLabel(outcome: string): string {
  switch (outcome) {
    case "CONFIRMED":
      return "Booked automatically";
    case "OFFERED":
      // Times went out and they have not picked one yet. It was falling through to the default and
      // rendering the raw enum on the card, beside outcomes that read as English.
      return "Times offered";
    case "HITL":
      return "Held for you";
    case "DENIED":
      return "Turned away";
    case "MOVED":
      return "Moved";
    case "CANCELLED":
      return "Cancelled";
    default:
      return outcome;
  }
}

/**
 * Why it was turned away, in a few words, beside the outcome.
 *
 * <p>Never names a rule. Not its ordinal, not its conditions, not how many there are. The host
 * wrote the policy and can read it; a request page that recites it back becomes a second copy of it
 * that drifts, and the drift is silent. Every line below is a fact about the request.</p>
 *
 * <p>An unrecognised code returns "" rather than throwing or rendering the raw string. A journal
 * enum that drifted Java to TypeScript has already taken a whole page down once; this one degrades
 * to the outcome badge the row already carries.</p>
 */
export function refusalLabel(code: string): string {
  switch (code) {
    case "NO_LINK_OPEN":
      return "Nothing was open";
    case "NO_RULES_YET":
      return "No rules yet";
    case "NOT_MATCHED":
      return "Not matched";
    case "NO_RULE_ADMITS":
      return "Not covered";
    case "NO_CALENDAR":
      return "No calendar connected";
    case "CALENDAR_UNREADABLE":
      return "Calendar could not be read";
    case "NO_TIMES_OFFERABLE":
      return "No times to offer";
    case "RULE_DENIED":
      return "Your rules said no";
    case "HOST_DECLINED":
      return "You declined";
    default:
      return "";
  }
}

/**
 * The same thing said properly, definitively, in one sentence.
 *
 * <p>The distinction worth holding onto is who decided. Four of these are nobody: the setup was
 * not open, or the model could not place what somebody wrote. A person asking for a perfectly
 * relevant meeting in words a classifier could not match reads identically to one asking for
 * something irrelevant, and the host is the only one who can tell them apart, so the copy says
 * plainly that no one decided rather than implying a judgement that was never made.</p>
 */
export function refusalNote(code: string): string {
  switch (code) {
    case "NO_LINK_OPEN":
      return "You had no booking link open to agents when they asked, so there was nothing to offer. Nobody decided against them.";
    case "NO_RULES_YET":
      return "The link this matched has no admission rules yet, so it can admit nobody. Nobody decided against them.";
    case "NOT_MATCHED":
      return "We could not match this to any link you have open. That can mean it was nothing you offer, and it can equally mean they described something you do offer in words we could not place. Nobody decided against them.";
    case "NO_RULE_ADMITS":
      return "None of your conditions covered this person. Nobody decided against them.";
    case "NO_CALENDAR":
      return "This matched one of your links and your conditions allowed it. You have no calendar connected, so there was nothing to check an hour against and nothing could be offered. Nobody decided against them.";
    case "CALENDAR_UNREADABLE":
      return "This matched one of your links and your conditions allowed it. Your connected calendar could not be read, so nothing could be offered. Check its permissions. Nobody decided against them.";
    case "NO_TIMES_OFFERABLE":
      return "This matched one of your links, and there were no hours to offer in the window they asked about. Nobody decided against them.";
    case "RULE_DENIED":
      return "Your conditions turned this request away.";
    case "HOST_DECLINED":
      return "You were shown this and said no.";
    default:
      return "";
  }
}

export function providerLabel(provider: string): string {
  switch (provider) {
    case "GOOGLE":
      return "Google";
    case "MICROSOFT":
      return "Microsoft";
    case "LINKEDIN":
      return "LinkedIn";
    default:
      return provider;
  }
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function slot(startsAt: string, endsAt?: string): string {
  const start = new Date(startsAt);
  const opening = start.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  if (!endsAt) return opening;
  const close = new Date(endsAt).toLocaleTimeString(undefined, { timeStyle: "short" });
  return `${opening}–${close}`;
}
